/**
 * StationManagementModals — Modal dialogs for SupervisorStationManagement.
 * Includes: Assign Session, Material Change Confirmation, Operator Prompt,
 * and Assign Operator modals.
 * Extracted from SupervisorStationManagement.tsx for maintainability.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { StationWithDetails, Operator } from '../../../services/stationManagementService';

interface StationManagementModalsProps {
  // Assign Session Modal
  showAssignSessionModal: boolean;
  selectedStation: StationWithDetails | null;
  selectedSessionId: string;
  onSelectedSessionIdChange: (id: string) => void;
  plannedSessions: any[];
  onConfirmAssignSession: () => void;
  onCloseAssignSession: () => void;
  assignSessionLoading: boolean;

  // Material Change Confirmation Modal
  showMaterialChangeConfirm: boolean;
  pendingMaterialChange: { stationId: number; newType: string } | null;
  stationMaterialTypes: Record<number, string>;
  onConfirmMaterialChange: () => void;
  onCancelMaterialChange: () => void;

  // Operator Prompt Modal (shown after session assigned)
  showOperatorPromptModal: boolean;
  stationForOperatorAssignment: StationWithDetails | null;
  selectedOperatorId: string;
  onSelectedOperatorIdChange: (id: string) => void;
  operators: Operator[] | undefined;
  onAssignOperator: (stationId: number, operatorId: string) => void;
  onCloseOperatorPrompt: () => void;
  assignOperatorLoading: boolean;

  // Assign Operator Modal (from station card button)
  showAssignOperatorModal: boolean;
  onCloseAssignOperator: () => void;
}

const StationManagementModals: React.FC<StationManagementModalsProps> = ({
  showAssignSessionModal,
  selectedStation,
  selectedSessionId,
  onSelectedSessionIdChange,
  plannedSessions,
  onConfirmAssignSession,
  onCloseAssignSession,
  assignSessionLoading,
  showMaterialChangeConfirm,
  pendingMaterialChange,
  stationMaterialTypes,
  onConfirmMaterialChange,
  onCancelMaterialChange,
  showOperatorPromptModal,
  stationForOperatorAssignment,
  selectedOperatorId,
  onSelectedOperatorIdChange,
  operators,
  onAssignOperator,
  onCloseOperatorPrompt,
  assignOperatorLoading,
  showAssignOperatorModal,
  onCloseAssignOperator,
}) => {
  const { t } = useLanguage();

  return (
    <>
      {/* Assign Session Modal */}
      {showAssignSessionModal && selectedStation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {t('supervisor.assignSessionTo')} {selectedStation.station_name}
              </h2>

              {selectedStation.session_number && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>{t('supervisor.warning')}:</strong> {t('supervisor.stationAlreadyHasSession')}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('supervisor.selectPlannedSession')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('supervisor.onlyPlannedSessionsShown')}
                </p>
                <select
                  value={selectedSessionId}
                  onChange={(e) => onSelectedSessionIdChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('supervisor.selectASession')}</option>
                  {plannedSessions.filter((s: any) => !s.station_id && s.status === 'received').map((session: any) => (
                    <option key={session.session_number} value={session.session_number}>
                      {session.session_number} - {session.product_name} ({Number(session.initial_material_weight || 0).toFixed(2)} lbs)
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={onCloseAssignSession}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={onConfirmAssignSession}
                  disabled={!selectedSessionId || assignSessionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignSessionLoading ? t('supervisor.assigning') : t('supervisor.assignSession')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material Change Confirmation Modal */}
      {showMaterialChangeConfirm && pendingMaterialChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                ⚠️ {t('supervisor.cleaningConfirmationRequired')}
              </h2>

              <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  {t('supervisor.materialChangeWarning')}
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('supervisor.previousType')}: <span className="text-red-600 dark:text-red-400">{stationMaterialTypes[pendingMaterialChange.stationId]}</span>
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('supervisor.newType')}: <span className="text-green-600 dark:text-green-400">{pendingMaterialChange.newType}</span>
                </p>
              </div>

              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                <p className="text-sm font-bold text-blue-900 dark:text-blue-200 mb-2">
                  {t('supervisor.confirmCleaning')}
                </p>
                <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>{t('supervisor.cleaningStep1')}</li>
                  <li>{t('supervisor.cleaningStep2')}</li>
                  <li>{t('supervisor.cleaningStep3')}</li>
                  <li>{t('supervisor.cleaningStep4')}</li>
                </ul>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={onCancelMaterialChange}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={onConfirmMaterialChange}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('supervisor.confirmCleaningComplete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operator Assignment Prompt Modal - shown after session is assigned */}
      {showOperatorPromptModal && stationForOperatorAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {t('supervisor.assignOperatorNow')}
              </h2>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {t('supervisor.sessionAssignedToStation').replace('{station}', stationForOperatorAssignment.station_name)}
              </p>

              <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  {t('supervisor.operatorRequiredToStart')}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('supervisor.selectOperator')}
                </label>
                <select
                  value={selectedOperatorId}
                  onChange={(e) => onSelectedOperatorIdChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('supervisor.selectAnOperator')}</option>
                  {operators?.filter((op: Operator) => op.disabled !== 'true').map((operator: Operator) => (
                    <option key={operator.user_id} value={operator.user_id}>
                      {operator.full_name} ({operator.employee_number})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={onCloseOperatorPrompt}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('supervisor.assignLater')}
                </button>
                <button
                  onClick={() => {
                    if (stationForOperatorAssignment && selectedOperatorId) {
                      onAssignOperator(stationForOperatorAssignment.station_id, selectedOperatorId);
                    }
                  }}
                  disabled={!selectedOperatorId || assignOperatorLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignOperatorLoading ? t('supervisor.assigning') : t('supervisor.assignOperator')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Operator Modal - triggered from station card button */}
      {showAssignOperatorModal && stationForOperatorAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {t('supervisor.assignOperatorTo')} {stationForOperatorAssignment.station_name}
              </h2>

              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>{t('supervisor.session')}:</strong> {stationForOperatorAssignment.sub_session_number || stationForOperatorAssignment.session_number}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>{t('supervisor.product')}:</strong> {stationForOperatorAssignment.product_name}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('supervisor.selectOperator')}
                </label>
                <select
                  value={selectedOperatorId}
                  onChange={(e) => onSelectedOperatorIdChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('supervisor.selectAnOperator')}</option>
                  {operators?.filter((op: Operator) => op.disabled !== 'true').map((operator: Operator) => (
                    <option key={operator.user_id} value={operator.user_id}>
                      {operator.full_name} ({operator.employee_number})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={onCloseAssignOperator}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    if (stationForOperatorAssignment && selectedOperatorId) {
                      onAssignOperator(stationForOperatorAssignment.station_id, selectedOperatorId);
                    }
                  }}
                  disabled={!selectedOperatorId || assignOperatorLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignOperatorLoading ? t('supervisor.assigning') : t('supervisor.assignOperator')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StationManagementModals;
